import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as carbonFactorsApi from '../api/carbonFactors';

export function CarbonReporting() {
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingFactor, setEditingFactor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterYear, setFilterYear] = useState('2024');
  const [form, setForm] = useState({
    territory: '',
    year: 2024,
    factorType: 'grid-electricity',
    value: '',
    unit: 'kg CO2e/kWh',
    source: '',
    effectiveDate: '',
    expirationDate: ''
  });

  const loadFactors = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (filterYear) filters.year = filterYear;
      const data = await carbonFactorsApi.getCarbonFactors(selectedOrg.id, filters);
      setFactors(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filterYear]);

  useEffect(() => {
    loadFactors();
  }, [loadFactors]);

  const handleOpenCreate = () => {
    setEditingFactor(null);
    setForm({
      territory: '',
      year: parseInt(filterYear) || 2024,
      factorType: 'grid-electricity',
      value: '',
      unit: 'kg CO2e/kWh',
      source: '',
      effectiveDate: `${filterYear}-01-01`,
      expirationDate: `${filterYear}-12-31`
    });
    setShowModal(true);
  };

  const handleOpenEdit = (factor) => {
    setEditingFactor(factor);
    setForm({
      territory: factor.territory,
      year: factor.year,
      factorType: factor.factorType,
      value: factor.value,
      unit: factor.unit,
      source: factor.source,
      effectiveDate: factor.effectiveDate,
      expirationDate: factor.expirationDate
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (editingFactor) {
        await carbonFactorsApi.updateCarbonFactor(selectedOrg.id, editingFactor.id, {
          ...form,
          value: parseFloat(form.value)
        });
      } else {
        await carbonFactorsApi.createCarbonFactor(selectedOrg.id, {
          ...form,
          value: parseFloat(form.value)
        });
      }
      setShowModal(false);
      loadFactors();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view carbon reporting.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Carbon Reporting"
        subtitle="Manage emission factors and generate carbon reports"
      />

      <div style={styles.toolbar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Year</label>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            style={styles.filterSelect}
            data-testid="year-filter"
          >
            <option value="2024">2024</option>
            <option value="2023">2023</option>
            <option value="2022">2022</option>
          </select>
        </div>
        {isAdmin && (
          <button
            onClick={handleOpenCreate}
            style={styles.createButton}
            data-testid="add-factor-button"
          >
            + Add Factor
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      <div style={styles.infoPanel}>
        <h4 style={styles.infoPanelTitle}>About Emission Factors</h4>
        <p style={styles.infoPanelText}>
          Emission factors convert energy consumption (kWh) to CO2 equivalent emissions. 
          Factors vary by territory and are updated annually based on grid mix data.
        </p>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading emission factors...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="factors-table">
            <thead>
              <tr>
                <th style={styles.th}>Territory</th>
                <th style={styles.th}>Year</th>
                <th style={styles.th}>Factor Type</th>
                <th style={styles.th}>Value</th>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Effective</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {factors.map(factor => (
                <tr key={factor.id} style={styles.tr} data-testid={`factor-${factor.id}`}>
                  <td style={styles.td}>
                    <span style={styles.territoryBadge}>{factor.territory}</span>
                  </td>
                  <td style={styles.td}>{factor.year}</td>
                  <td style={styles.td}>{factor.factorType}</td>
                  <td style={styles.td}>
                    <strong>{factor.value}</strong>
                  </td>
                  <td style={styles.td}>{factor.unit}</td>
                  <td style={styles.td}>{factor.source}</td>
                  <td style={styles.td}>
                    {factor.effectiveDate} to {factor.expirationDate}
                  </td>
                  <td style={styles.td}>
                    {isAdmin && (
                      <button
                        onClick={() => handleOpenEdit(factor)}
                        style={styles.editButton}
                        data-testid={`edit-${factor.id}`}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {factors.length === 0 && (
                <tr>
                  <td colSpan="8" style={styles.emptyState}>No emission factors found for {filterYear}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Carbon Report Features</h3>
        <div style={styles.featuresGrid}>
          <div style={styles.featureCard}>
            <h4 style={styles.featureTitle}>Territory-Based Factors</h4>
            <p style={styles.featureDesc}>
              Emission factors are configured by territory (state/region) for accurate local calculations.
            </p>
          </div>
          <div style={styles.featureCard}>
            <h4 style={styles.featureTitle}>Time-Based Versioning</h4>
            <p style={styles.featureDesc}>
              Factors are versioned by year with effective dates to ensure historical accuracy.
            </p>
          </div>
          <div style={styles.featureCard}>
            <h4 style={styles.featureTitle}>Assumptions Captured</h4>
            <p style={styles.featureDesc}>
              All assumptions and factor sources are documented and included in report outputs.
            </p>
          </div>
          <div style={styles.featureCard}>
            <h4 style={styles.featureTitle}>Audit Trail</h4>
            <p style={styles.featureDesc}>
              Changes to factors are logged for compliance and reproducibility of past reports.
            </p>
          </div>
        </div>
      </div>

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="factor-modal">
            <h3 style={styles.modalTitle}>
              {editingFactor ? 'Edit Emission Factor' : 'Add Emission Factor'}
            </h3>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Territory</label>
                <input
                  type="text"
                  value={form.territory}
                  onChange={(e) => setForm(prev => ({ ...prev, territory: e.target.value }))}
                  style={styles.input}
                  placeholder="e.g., US-CA"
                  data-testid="territory-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Year</label>
                <input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  style={styles.input}
                  data-testid="year-input"
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Factor Type</label>
                <select
                  value={form.factorType}
                  onChange={(e) => setForm(prev => ({ ...prev, factorType: e.target.value }))}
                  style={styles.select}
                  data-testid="factor-type-input"
                >
                  <option value="grid-electricity">Grid Electricity</option>
                  <option value="gasoline-displaced">Gasoline Displaced</option>
                  <option value="diesel-displaced">Diesel Displaced</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Value</label>
                <input
                  type="number"
                  step="0.001"
                  value={form.value}
                  onChange={(e) => setForm(prev => ({ ...prev, value: e.target.value }))}
                  style={styles.input}
                  placeholder="e.g., 0.385"
                  data-testid="value-input"
                />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Unit</label>
              <input
                type="text"
                value={form.unit}
                onChange={(e) => setForm(prev => ({ ...prev, unit: e.target.value }))}
                style={styles.input}
                data-testid="unit-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Source</label>
              <input
                type="text"
                value={form.source}
                onChange={(e) => setForm(prev => ({ ...prev, source: e.target.value }))}
                style={styles.input}
                placeholder="e.g., EPA eGRID 2024"
                data-testid="source-input"
              />
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Effective Date</label>
                <input
                  type="date"
                  value={form.effectiveDate}
                  onChange={(e) => setForm(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  style={styles.input}
                  data-testid="effective-date-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Expiration Date</label>
                <input
                  type="date"
                  value={form.expirationDate}
                  onChange={(e) => setForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                  style={styles.input}
                  data-testid="expiration-date-input"
                />
              </div>
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.territory || !form.value}
                style={styles.submitButton}
                data-testid="save-factor-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1rem' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white' },
  createButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #38a169', background: '#38a169', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  infoPanel: { background: '#ebf8ff', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', borderLeft: '4px solid #3182ce' },
  infoPanelTitle: { margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2b6cb0' },
  infoPanelText: { margin: 0, fontSize: '0.75rem', color: '#2c5282' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto', marginBottom: '2rem' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  territoryBadge: { padding: '0.25rem 0.5rem', background: '#c6f6d5', color: '#276749', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  section: { marginTop: '2rem' },
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#2d3748' },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' },
  featureCard: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #38a169' },
  featureTitle: { margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  featureDesc: { margin: 0, fontSize: '0.75rem', color: '#718096', lineHeight: '1.5' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '500px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  formGroup: { marginBottom: '1rem', flex: 1 },
  formRow: { display: 'flex', gap: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  select: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #38a169', background: '#38a169', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
